import { useState } from "react";

import api from "../axios/axiosInstance";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";


const useProgressCardHandlers = ({
    selectedStudent,
    selectedSubject,
    selectedUnit,
    selectedTerm,
    setReportCards,
    setIsDirty,
    setOverallAssessment,
    setEducatorComment,
}) => {

    const [showWarning, setShowWarning] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });


    // =========================================================
    // TOAST
    // =========================================================

    const showToast = (
        message,
        severity = "info"
    ) => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };


    const SnackbarComponent = () => (
        <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() =>
                setSnackbar((prev) => ({
                    ...prev,
                    open: false,
                }))
            }
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
        >
            <Alert
                severity={snackbar.severity}
                onClose={() =>
                    setSnackbar((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
                sx={{
                    width: "100%",
                }}
            >
                {snackbar.message}
            </Alert>
        </Snackbar>
    );


    // =========================================================
    // RETRIEVE PROGRESS CARD
    // =========================================================

    const handleRetrieveReportCard =
        async () => {

            const missing = [];

            if (!selectedStudent) {
                missing.push("Student");
            }

            if (!selectedSubject) {
                missing.push("Subject");
            }

            if (!selectedUnit) {
                missing.push("Unit");
            }

            if (
                ![1, 2, 3].includes(
                    parseInt(selectedTerm, 10)
                )
            ) {
                missing.push("Term Number");
            }

            if (missing.length > 0) {
                setMissingFields(missing);
                setShowWarning(true);
                return;
            }

            try {
                const response =
                    await api.get(
                        `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/retrieve/`,
                        {
                            params: {
                                student:
                                    selectedStudent,

                                subject:
                                    selectedSubject,

                                unit:
                                    selectedUnit,

                                term_number:
                                    parseInt(
                                        selectedTerm,
                                        10
                                    ),
                            },

                            withCredentials:
                                true,
                        }
                    );

                const card =
                    Array.isArray(
                        response.data
                    )
                        ? response.data[0]
                        : response.data;

                if (card) {
                    setReportCards(card);

                    setOverallAssessment(
                        card.overall_assessment ||
                            ""
                    );

                    setEducatorComment?.(
                        card.educator_comment ||
                            ""
                    );

                    setIsDirty(false);

                } else {
                    setReportCards(null);

                    setOverallAssessment("");

                    setEducatorComment?.("");

                    setIsDirty(false);
                }

            } catch (error) {
                console.error(
                    "❌ Failed to retrieve progress card:",
                    error
                );
            }
        };


    // =========================================================
    // CHECK EXISTING CARD
    // =========================================================

    const checkIfProgressCardExists =
        async () => {

            try {
                const response =
                    await api.get(
                        `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/retrieve/`,
                        {
                            params: {
                                student:
                                    selectedStudent,

                                subject:
                                    selectedSubject,

                                unit:
                                    selectedUnit,

                                term_number:
                                    parseInt(
                                        selectedTerm,
                                        10
                                    ),
                            },

                            withCredentials:
                                true,
                        }
                    );

                const result =
                    Array.isArray(
                        response.data
                    )
                        ? response.data[0]
                        : response.data;

                return !!(
                    result &&
                    result.id
                );

            } catch (error) {
                console.error(
                    "❌ Error checking progress card existence:",
                    error
                );

                return false;
            }
        };


    // =========================================================
    // UPDATE ENTRY LOCALLY
    // =========================================================

    const handleEntryUpdate = (
        index,
        field,
        value
    ) => {

        setReportCards((prev) => {
            if (
                !prev ||
                !Array.isArray(prev.entries)
            ) {
                return prev;
            }

            const updatedEntries =
                prev.entries.map(
                    (entry, idx) =>
                        idx === index
                            ? {
                                ...entry,
                                [field]:
                                    value,
                            }
                            : entry
                );

            return {
                ...prev,
                entries:
                    updatedEntries,
            };
        });

        setIsDirty(true);
    };


    // =========================================================
    // CREATE PROGRESS CARD
    // =========================================================

    const handleCreateProgressCard =
        async ({
            reportCards,
            setReportCards,
            overall_assessment,
            term_number,
            educator_comment,
        }) => {

            if (
                ![1, 2, 3].includes(
                    term_number
                )
            ) {
                showToast(
                    "❌ Invalid term number. Please select Term 1, 2, or 3.",
                    "error"
                );

                return;
            }

            if (!overall_assessment) {
                showToast(
                    "⚠️ Please select an overall assessment.",
                    "warning"
                );

                return;
            }

            const entries =
                reportCards?.entries || [];

            if (
                !Array.isArray(entries) ||
                entries.length === 0
            ) {
                showToast(
                    "⚠️ No skills are available for this unit.",
                    "warning"
                );

                return;
            }

            const invalidSkill =
                entries.some(
                    (entry) =>
                        !entry.skill
                );

            if (invalidSkill) {
                showToast(
                    "❌ One or more assessment skills are invalid. Please reload the unit.",
                    "error"
                );

                return;
            }

            const exists =
                await checkIfProgressCardExists();

            if (exists) {
                setShowDuplicateDialog(true);
                return;
            }

            // IMPORTANT:
            // Backend derives DOK and wording
            // from the master Skill.
            const payload = {
                student:
                    selectedStudent,

                subject:
                    selectedSubject,

                unit:
                    selectedUnit,

                overall_assessment,

                educator_comment,

                term_number,

                // assessment_status is computed server-side from
                // the Skill entries — it is not sent from here.

                dok_assessments: (reportCards?.dok_assessments || []).map((item) => ({
                    dok_level: item.dok_level.level,
                    proficiency: item.proficiency || "",
                })),

                entries:
                    entries.map(
                        (entry) => ({
                            skill:entry.skill,

                            is_achieved:Boolean(entry.is_achieved),
                        })
                    ),
            };

            try {
                const response =
                    await api.post(
                        `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/create/`,
                        payload,
                        {
                            headers: {
                                "Content-Type":
                                    "application/json",
                            },

                            withCredentials:
                                true,
                        }
                    );

                showToast(
                    "✅ Assessment Report Created!",
                    "success"
                );

                setReportCards(
                    response.data
                );

                setIsDirty(false);

            } catch (error) {
                console.error(
                    "❌ Error creating assessment report:",
                    error
                );

                const message =
                    error?.response
                        ?.data?.error ||
                    "Failed to create assessment report.";

                showToast(
                    `❌ ${message}`,
                    "error"
                );
            }
        };


    // =========================================================
    // UPDATE PROGRESS CARD
    // =========================================================

    const handleUpdateProgressCard =
        async ({
            reportCards,
            setReportCards,
            overall_assessment,
            term_number,
            educator_comment,
        }) => {

            if (
                ![1, 2, 3].includes(
                    term_number
                )
            ) {
                showToast(
                    "❌ Invalid term number. Please select Term 1, 2, or 3.",
                    "error"
                );

                return;
            }

            if (!overall_assessment) {
                showToast(
                    "⚠️ Please select an overall assessment.",
                    "warning"
                );

                return;
            }

            const entries =
                reportCards?.entries || [];

            if (
                !Array.isArray(entries) ||
                entries.length === 0
            ) {
                showToast(
                    "⚠️ No skills are available to update.",
                    "warning"
                );

                return;
            }

            const invalidSkill =
                entries.some(
                    (entry) =>
                        !entry.skill
                );

            if (invalidSkill) {
                showToast(
                    "❌ One or more assessment skills are invalid. Please reload the unit.",
                    "error"
                );

                return;
            }

            const updatedEntries =
                entries.map(
                    (entry) => ({
                        skill:entry.skill,

                        is_achieved:Boolean(entry.is_achieved),
                    })
                );

            const updatePayload = {
                entries:updatedEntries,

                overall_assessment,
                // assessment_status is computed server-side from
                // the Skill entries — it is not sent from here.
                dok_assessments:
                    (reportCards?.dok_assessments || []).map((item) => ({
                        dok_level: item.dok_level.level,
                        proficiency: item.proficiency || "",
                    })),
                term_number,

                educator_comment,
            };

            try {
                const response =
                    await api.patch(
                        `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/update/${reportCards.id}/`,
                        updatePayload,
                        {
                            headers: {
                                "Content-Type":
                                    "application/json",
                            },

                            withCredentials:
                                true,
                        }
                    );

                showToast(
                    "✅ Assessment Report Updated!",
                    "success"
                );

                setReportCards(
                    response.data
                );

                setIsDirty(false);

            } catch (error) {
                console.error(
                    "❌ Error updating assessment report:",
                    error
                );

                const message =
                    error?.response
                        ?.data?.error ||
                    "Failed to update assessment report.";

                showToast(
                    `❌ ${message}`,
                    "error"
                );
            }
        };


    // =========================================================
    // RETURN
    // =========================================================

    return {
        handleRetrieveReportCard,
        handleEntryUpdate,
        handleCreateProgressCard,
        handleUpdateProgressCard,

        showWarning,
        setShowWarning,

        missingFields,
        setMissingFields,

        showDuplicateDialog,
        setShowDuplicateDialog,

        SnackbarComponent,
    };
};


export default useProgressCardHandlers;